import type { EntityState } from '../../lib/api.js';
import { useDevices } from '../../context/DevicesContext.js';
import { Slider } from '../ui/Slider.js';
import { friendlyName } from '../../lib/utils.js';
import { useToast } from '../ui/Toast.js';

interface Props {
  entityId: string;
  entity: EntityState;
}

export function ClimateCard({ entityId, entity }: Props) {
  const { sendCommand, patchEntity } = useDevices();
  const toast = useToast();
  const target =
    typeof entity.attributes['temperature'] === 'number'
      ? (entity.attributes['temperature'] as number)
      : 20;
  const current =
    typeof entity.attributes['current_temperature'] === 'number'
      ? (entity.attributes['current_temperature'] as number)
      : null;
  const humidity =
    typeof entity.attributes['current_humidity'] === 'number'
      ? (entity.attributes['current_humidity'] as number)
      : null;

  const handleTemp = async (v: number) => {
    const prev = entity;
    patchEntity(entityId, { ...entity, attributes: { ...entity.attributes, temperature: v } });
    try {
      await sendCommand('climate', 'set_temperature', entityId, { temperature: v });
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    }
  };

  return (
    <div
      className="p-4 rounded-xl border flex flex-col gap-2"
      style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
    >
      <div className="flex justify-between">
        <div className="font-medium text-[var(--ac-text)]">
          {friendlyName(entityId, entity.attributes)}
        </div>
        <div className="text-xs text-[var(--ac-text-muted)]">{entity.state}</div>
      </div>
      <div className="flex gap-4 text-sm text-[var(--ac-text-muted)]">
        {current !== undefined && current !== null ? <span>Now: {current}°</span> : null}
        {humidity !== undefined && humidity !== null ? <span>Humidity: {humidity}%</span> : null}
      </div>
      <Slider value={target} min={10} max={32} step={0.5} onChange={handleTemp} label="Target" />
    </div>
  );
}
